import React, { useState, useEffect } from 'react';
import { Alert, Badge, Card, Collapse, Button, Row, Col } from 'react-bootstrap';
import { 
  FaClock, 
  FaFileAlt, 
  FaExclamationCircle, 
  FaCheckCircle, 
  FaChevronDown,
  FaChevronRight,
  FaPlayCircle,
  FaStopCircle,
  FaInfoCircle,
  FaCode,
  FaListUl,
  FaDatabase,
  FaCogs,
  FaExchangeAlt
} from 'react-icons/fa';
import Spinner from './Spinner';

/**
 * Safe render utility to ensure objects are never passed directly to React
 */
const safeRenderValue = (value) => {
  if (value === null || value === undefined) {
    return 'No data available';
  }
  
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return '[Object - Cannot display]';
    }
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  // For any other type, convert to string
  return String(value);
};

const ExecutionLogsWindow = ({ executionArn, agentId, targetType, result, refreshInterval = 5000 }) => {
  const [formattedLogs, setFormattedLogs] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    output: true,
    metadata: true,
    inputOutput: true,
    stateDetails: false,
    timeline: false,
    raw: false
  });

  // Update formatted logs when result prop changes
  useEffect(() => {
    if (result) {
      try {
        console.log('Formatting execution logs for better readability...');
        const formatted = formatExecutionResponse(result);
        setFormattedLogs(formatted);
        setIsLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error formatting logs:', err);
        setError('Error formatting logs: ' + err.message);
        setIsLoading(false);
      }
    }
  }, [result]);

  // Clear logs when executionArn changes
  useEffect(() => {
    setFormattedLogs(null);
    setIsLoading(true);
    setError(null);
  }, [executionArn]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Enhanced format function to extract and organize the response data
  const formatExecutionResponse = (rawResult) => {
    try {
      let parsedResult = rawResult;
      
      // If it's a string, try to parse it
      if (typeof rawResult === 'string') {
        parsedResult = JSON.parse(rawResult);
      }

      console.log('Parsing execution result for formatting:', parsedResult);

      // Extract the organized execution information
      const executionInfo = {
        timestamp: new Date().toLocaleTimeString(),
        agentOutput: null,
        executionSummary: null,
        organizationData: null
      };

      // Parse the main response structure
      if (parsedResult.body && typeof parsedResult.body === 'string') {
        try {
          const bodyData = JSON.parse(parsedResult.body);
          console.log('Parsed body data:', bodyData);
          
          // Extract output information
          if (bodyData.output) {
            executionInfo.agentOutput = bodyData.output.agentOutput;
            executionInfo.executionSummary = bodyData.output.executionSummary;
          }
          
          // Organize the response data according to the requested format
          executionInfo.organizationData = organizeResponseData(bodyData);
        } catch (parseError) {
          console.warn('Could not parse body as JSON, using raw format');
          executionInfo.organizationData = organizeResponseData(parsedResult);
        }
      } else if (parsedResult.status) {
        // Direct response format
        executionInfo.agentOutput = parsedResult.output?.agentOutput;
        executionInfo.executionSummary = parsedResult.output?.executionSummary;
        executionInfo.organizationData = organizeResponseData(parsedResult);
      }

      return executionInfo;
    } catch (error) {
      console.error('Error in formatExecutionResponse:', error);
      throw error;
    }
  };

  // New function to organize response data according to the requested format
  const organizeResponseData = (data) => {
    try {
      const organizedData = {
        metadata: {},
        inputOutput: {},
        stateDetails: []
      };

      // A) Extract Metadata of the step function
      if (data.executionArn) organizedData.metadata.executionArn = data.executionArn;
      if (data.stateMachineArn) organizedData.metadata.stateMachineArn = data.stateMachineArn;
      if (data.name) organizedData.metadata.name = data.name;
      if (data.status) organizedData.metadata.status = data.status;
      if (data.startDate) organizedData.metadata.startDate = data.startDate;
      if (data.stopDate) organizedData.metadata.stopDate = data.stopDate;

      // B) Extract Input and Output
      if (data.input) organizedData.inputOutput.input = data.input;
      if (data.output) organizedData.inputOutput.output = data.output;

      // C) Extract State Details
      if (data.stateDetails && Array.isArray(data.stateDetails)) {
        organizedData.stateDetails = data.stateDetails.map(state => {
          const organizedState = {};
          
          // Extract relevant state information
          if (state.stateName) organizedState.stateName = state.stateName;
          if (state.stateType) organizedState.stateType = state.stateType;
          if (state.enterTime) organizedState.enterTime = state.enterTime;
          if (state.exitTime) organizedState.exitTime = state.exitTime;
          if (state.status) organizedState.status = state.status;
          if (state.durationSeconds) organizedState.durationSeconds = state.durationSeconds;
          if (state.events) organizedState.events = state.events;
          if (state.input) organizedState.input = state.input;
          if (state.output) organizedState.output = state.output;
          
          return organizedState;
        });
      }

      return organizedData;
    } catch (error) {
      console.error('Error organizing response data:', error);
      return { metadata: {}, inputOutput: {}, stateDetails: [] };
    }
  };

  // FIXED: Format execution summary for display - Enhanced to prevent object rendering
  const formatExecutionSummary = (summary) => {
    if (!summary) return null;
    
    try {
      // FIXED: Use safeRenderValue first to ensure we have a string
      let summaryStr = safeRenderValue(summary);
      
      // Now safely apply string transformations
      return summaryStr
        .replace(/\\\\\\\\n/g, '\\\\n')
        .split('\\\\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    } catch (error) {
      console.error('Error formatting execution summary:', error);
      return ['Error formatting execution summary'];
    }
  };

  // Format duration in a human-readable way
  const formatDuration = (durationSeconds) => {
    if (!durationSeconds) return 'N/A';
    
    if (durationSeconds < 60) {
      return `${durationSeconds.toFixed(2)}s`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = (durationSeconds % 60).toFixed(2);
      return `${minutes}m ${seconds}s`;
    }
  };

  if (!executionArn && !result) {
    return (
      <div className="execution-logs-container p-4">
        <div className="text-center py-4">
          <FaClock className="me-2" />
          <span>Waiting for execution to start...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="execution-logs-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="logs-header p-3 border-bottom bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FaFileAlt className="me-2" />
            <h6 className="mb-0">Execution Details</h6>
          </div>
          {isLoading && (
            <div className="d-flex align-items-center">
              <Spinner size="sm" className="me-2" />
              <small className="text-muted">Formatting output...</small>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="danger" className="m-3">
          <FaExclamationCircle className="me-2" />
          {error}
        </Alert>
      )}

      {/* Formatted Content */}
      <div 
        className="logs-content flex-grow-1 p-3"
        style={{ 
          minHeight: '300px',
          maxHeight: '600px',
          overflowY: 'auto',
          backgroundColor: '#f8f9fa'
        }}
      >
        {isLoading && !formattedLogs ? (
          <div className="text-center py-4">
            <Spinner />
            <p className="mt-3">Processing execution data...</p>
          </div>
        ) : formattedLogs ? (
          <div className="formatted-execution-data">

            {/* Agent Output Section - FIXED to prevent object rendering */}
            {formattedLogs.agentOutput && (
              <Card className="mb-3">
                <Card.Header 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSection('output')}
                >
                  <div className="d-flex align-items-center">
                    <FaCheckCircle className="me-2 text-success" />
                    <strong>Agent Result</strong>
                  </div>
                  {expandedSections.output ? <FaChevronDown /> : <FaChevronRight />}
                </Card.Header>
                <Collapse in={expandedSections.output}>
                  <Card.Body>
                    <div className="p-3 bg-white rounded border">
                      <div className="result-output" style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                        {/* FIXED: Always use safeRenderValue to prevent object rendering */}
                        {safeRenderValue(formattedLogs.agentOutput)}
                      </div>
                    </div>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* Execution Summary Section - FIXED to prevent object rendering */}
            {formattedLogs.executionSummary && (
              <Card className="mb-3">
                <Card.Header 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSection('summary')}
                >
                  <div className="d-flex align-items-center">
                    <FaListUl className="me-2 text-info" />
                    <strong>Execution Summary</strong>
                  </div>
                  {expandedSections.summary ? <FaChevronDown /> : <FaChevronRight />}
                </Card.Header>
                <Collapse in={expandedSections.summary}>
                  <Card.Body>
                    <div className="execution-summary">
                      {/* FIXED: Use safeRenderValue and check if result is array */}
                      {(() => {
                        const summaryLines = formatExecutionSummary(formattedLogs.executionSummary);
                        if (Array.isArray(summaryLines)) {
                          return summaryLines.map((line, index) => (
                            <div key={index} className="summary-line mb-2">
                              {safeRenderValue(line)}
                            </div>
                          ));
                        } else {
                          return (
                            <div className="summary-line mb-2">
                              {safeRenderValue(summaryLines)}
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* Organized Response Data - New structured format */}
            <Card className="mb-3">
              <Card.Header 
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleSection('raw')}
              >
                <div className="d-flex align-items-center">
                  <FaCode className="me-2 text-muted" />
                  <strong>Raw Response Data</strong>
                </div>
                {expandedSections.raw ? <FaChevronDown /> : <FaChevronRight />}
              </Card.Header>
              <Collapse in={expandedSections.raw}>
                <Card.Body>
                  {formattedLogs.organizationData ? (
                    <div>
                      {/* A) Metadata of the step function */}
                      {formattedLogs.organizationData.metadata && Object.keys(formattedLogs.organizationData.metadata).length > 0 && (
                        <div className="mb-4">
                          <h6 className="d-flex align-items-center mb-3">
                            <FaDatabase className="me-2 text-primary" />
                            A) Metadata of the Step Function
                          </h6>
                          <div className="p-3 bg-light rounded">
                            <pre style={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              margin: 0,
                              fontSize: '0.9rem'
                            }}>
                              {safeRenderValue(formattedLogs.organizationData.metadata)}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* B) Input and Output */}
                      {formattedLogs.organizationData.inputOutput && Object.keys(formattedLogs.organizationData.inputOutput).length > 0 && (
                        <div className="mb-4">
                          <h6 className="d-flex align-items-center mb-3">
                            <FaExchangeAlt className="me-2 text-success" />
                            B) Input and Output
                          </h6>
                          <div className="p-3 bg-light rounded">
                            <pre style={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              margin: 0,
                              fontSize: '0.9rem'
                            }}>
                              {safeRenderValue(formattedLogs.organizationData.inputOutput)}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* C) State Details - FIXED: Comprehensive badge styling */}
                      {formattedLogs.organizationData.stateDetails && formattedLogs.organizationData.stateDetails.length > 0 && (
                        <div className="mb-4">
                          <Card>
                            <Card.Header 
                              className="d-flex justify-content-between align-items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleSection('stateDetails')}
                            >
                              <h6 className="d-flex align-items-center mb-0" style={{ gap: '8px', flexWrap: 'nowrap' }}>
                                <FaCogs className="text-warning" style={{ flexShrink: 0 }} />
                                <span style={{ flexShrink: 0 }}>C) State Details</span>
                                <Badge 
                                  bg="secondary" 
                                  style={{
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: '12px',
                                    flexShrink: 0
                                  }}
                                >
                                  {formattedLogs.organizationData.stateDetails.length} states
                                </Badge>
                              </h6>
                              {expandedSections.stateDetails ? <FaChevronDown /> : <FaChevronRight />}
                            </Card.Header>
                            <Collapse in={expandedSections.stateDetails}>
                              <Card.Body>
                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                  {formattedLogs.organizationData.stateDetails.map((state, index) => (
                                    <Card key={index} className="mb-3 border">
                                      <Card.Header className="py-2">
                                        <div className="d-flex justify-content-between align-items-center" style={{ gap: '8px', flexWrap: 'nowrap' }}>
                                          <div className="d-flex align-items-center" style={{ gap: '8px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                                            <strong style={{ flexShrink: 0 }}>{safeRenderValue(state.stateName)}</strong>
                                            {state.stateType && (
                                              <Badge 
                                                bg="info"
                                                style={{
                                                  whiteSpace: 'nowrap',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  padding: '4px 10px',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  borderRadius: '12px',
                                                  flexShrink: 0
                                                }}
                                              >
                                                {safeRenderValue(state.stateType)}
                                              </Badge>
                                            )}
                                            {state.status && (
                                              <Badge 
                                                bg={state.status === 'SUCCEEDED' ? 'success' : state.status === 'FAILED' ? 'danger' : 'secondary'}
                                                style={{
                                                  whiteSpace: 'nowrap',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  padding: '4px 10px',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  borderRadius: '12px',
                                                  flexShrink: 0
                                                }}
                                              >
                                                {safeRenderValue(state.status)}
                                              </Badge>
                                            )}
                                          </div>
                                          {state.durationSeconds && (
                                            <small className="text-muted" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                              Duration: {formatDuration(state.durationSeconds)}
                                            </small>
                                          )}
                                        </div>
                                      </Card.Header>
                                      <Card.Body className="py-2">
                                        <Row>
                                          {state.enterTime && (
                                            <Col md={6}>
                                              <small>
                                                <strong>Enter Time:</strong> {new Date(state.enterTime).toLocaleTimeString()}
                                              </small>
                                            </Col>
                                          )}
                                          {state.exitTime && (
                                            <Col md={6}>
                                              <small>
                                                <strong>Exit Time:</strong> {new Date(state.exitTime).toLocaleTimeString()}
                                              </small>
                                            </Col>
                                          )}
                                        </Row>
                                        
                                        {/* State Input - FIXED to prevent object rendering */}
                                        {state.input && Object.keys(state.input).length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-primary mb-2">Input:</h6>
                                            <div className="p-2 bg-light rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                              <pre className="mb-0" style={{ fontSize: '0.8rem' }}>
                                                {safeRenderValue(state.input)}
                                              </pre>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* State Output - FIXED to prevent object rendering */}
                                        {state.output && Object.keys(state.output).length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-success mb-2">Output:</h6>
                                            <div className="p-2 bg-light rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                              <pre className="mb-0" style={{ fontSize: '0.8rem' }}>
                                                {safeRenderValue(state.output)}
                                              </pre>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Events if any */}
                                        {state.events && state.events.length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-info mb-2">Events:</h6>
                                            <div className="p-2 bg-light rounded" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                              <small>
                                                {state.events.length} event(s)
                                              </small>
                                            </div>
                                          </div>
                                        )}
                                      </Card.Body>
                                    </Card>
                                  ))}
                                </div>
                              </Card.Body>
                            </Collapse>
                          </Card>
                        </div>
                      )}

                      {/* Fallback: If no organized data, show original raw data - FIXED to prevent object rendering */}
                      {(!formattedLogs.organizationData || 
                        (Object.keys(formattedLogs.organizationData.metadata).length === 0 && 
                         Object.keys(formattedLogs.organizationData.inputOutput).length === 0 && 
                         formattedLogs.organizationData.stateDetails.length === 0)) && (
                        <div className="mb-4">
                          <h6 className="text-muted mb-3">Complete Response Data</h6>
                          <div className="p-3 bg-light rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <pre style={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              margin: 0,
                              fontSize: '0.8rem',
                              lineHeight: '1.3'
                            }}>
                              {safeRenderValue(result)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 bg-light rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <pre style={{ 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        margin: 0,
                        fontSize: '0.8rem',
                        lineHeight: '1.3'
                      }}>
                        {safeRenderValue(result)}
                      </pre>
                    </div>
                  )}
                </Card.Body>
              </Collapse>
            </Card>

            {/* Quick Actions */}
            <div className="text-center mt-3">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  setExpandedSections({
                    summary: true,
                    output: true,
                    metadata: true,
                    inputOutput: true,
                    stateDetails: true,
                    timeline: false,
                    raw: true
                  });
                }}
              >
                Expand All Sections
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm"
                className="ms-2"
                onClick={() => {
                  setExpandedSections({
                    summary: true,
                    output: true,
                    metadata: true,
                    inputOutput: true,
                    stateDetails: false,
                    timeline: false,
                    raw: false
                  });
                }}
              >
                Collapse to Summary
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <FaClock size={24} className="mb-2" />
            <p>No execution data available yet. Results will appear as execution progresses.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="logs-footer p-2 border-top bg-light">
        <small className="text-muted">
          <FaFileAlt className="me-1" />
          {formattedLogs ? 
            `Updated at ${formattedLogs.timestamp} - Click sections to expand/collapse` : 
            'Waiting for execution data...'
          }
        </small>
      </div>
    </div>
  );
};

export default ExecutionLogsWindow;